import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  type HttpInterceptorFn,
  type HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  finalize,
  type Observable,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { environment } from '../../../environments/environment.development';
import type { TokensResponse } from '../../features/auth/models/auth.models';
import { TokenService } from '../services/token/token.service';
import { SKIP_AUTH } from './auth-http-context';

const REFRESH_TOKENS_URL =
  `${environment.authUrl}/refresh-tokens`;

const CHANGE_PASSWORD_URL =
  `${environment.authUrl}/change-password`;

let refreshRequest$:
  | Observable<TokensResponse>
  | null = null;

export const authInterceptor:
  HttpInterceptorFn = (
    request,
    next,
  ) => {
    const tokenService =
      inject(TokenService);
    const httpBackend =
      inject(HttpBackend);
    const router = inject(Router);

    if (
      request.context.get(SKIP_AUTH) ||
      !isBackendRequest(request.url)
    ) {
      return next(request);
    }

    const accessToken =
      tokenService.getAccessToken();

    const authorizedRequest =
      accessToken
        ? addBearerToken(
            request,
            accessToken,
          )
        : request;

    return next(
      authorizedRequest,
    ).pipe(
      catchError(
        (
          error: unknown,
        ) => {
          if (
            !shouldRefresh(
              error,
              request,
              tokenService,
            )
          ) {
            return throwError(
              () => error,
            );
          }

          return refreshTokens(
            tokenService,
            httpBackend,
          ).pipe(
            switchMap(
              (tokens) =>
                next(
                  addBearerToken(
                    request,
                    tokens.access_token,
                  ),
                ),
            ),
            catchError(
              (
                refreshError:
                  unknown,
              ) => {
                tokenService.clearTokens();

                void router.navigate(
                  ['/auth/login'],
                  {
                    queryParams: {
                      returnUrl:
                        router.url,
                    },
                  },
                );

                return throwError(
                  () =>
                    refreshError,
                );
              },
            ),
          );
        },
      ),
    );
  };

function refreshTokens(
  tokenService: TokenService,
  httpBackend: HttpBackend,
): Observable<TokensResponse> {
  if (refreshRequest$) {
    return refreshRequest$;
  }

  const refreshToken =
    tokenService.getRefreshToken();

  if (!refreshToken) {
    return throwError(
      () =>
        new Error(
          'Refresh token отсутствует.',
        ),
    );
  }

  const http = new HttpClient(
    httpBackend,
  );

  const headers = new HttpHeaders({
    Authorization:
      `Bearer ${refreshToken}`,
  });

  refreshRequest$ = http
    .post<TokensResponse>(
      REFRESH_TOKENS_URL,
      {},
      { headers },
    )
    .pipe(
      tap((tokens) =>
        tokenService.setTokens(
          tokens,
          tokenService.getAccountRole() ??
            undefined,
        ),
      ),
      finalize(() => {
        refreshRequest$ = null;
      }),
      shareReplay({
        bufferSize: 1,
        refCount: false,
      }),
    );

  return refreshRequest$;
}

function shouldRefresh(
  error: unknown,
  request: HttpRequest<unknown>,
  tokenService: TokenService,
): boolean {
  return (
    error instanceof
      HttpErrorResponse &&
    error.status === 401 &&
    !request.url.startsWith(
      REFRESH_TOKENS_URL,
    ) &&
    !request.url.startsWith(
      CHANGE_PASSWORD_URL,
    ) &&
    Boolean(
      tokenService.getRefreshToken(),
    )
  );
}

function addBearerToken(
  request: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization:
        `Bearer ${token}`,
    },
  });
}

function isBackendRequest(
  url: string,
): boolean {
  const bases = [
    environment.apiV1Url,
    environment.authUrl,
    environment.cdnUrl,
  ].map((value) =>
    value.replace(/\/+$/, ''),
  );

  return bases.some(
    (base) =>
      Boolean(base) &&
      url.startsWith(base),
  );
}
